import { useEffect, useState } from "react";
import { FileDown, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const formatEuro = (cents) =>
  (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const choiceBtnStyle = (active) => ({
  padding: "9px 26px",
  borderRadius: 10,
  border: active ? "1px solid #93c5fd" : "1px solid #334155",
  background: active ? "#1d4ed8" : "#1e293b",
  color: "#f1f5f9",
  fontSize: 14.5,
  fontWeight: 600,
  cursor: "pointer",
});

// D12 (Tür 1) – additives Kauf-Panel für den anonymen Einmalkauf (`einmal_pdf`).
// Erscheint unter der read-only Vorschau. Kein Konto, keine Anmeldung. Der Content
// (die gesehene Welt) wird beim Kauf serverseitig persistiert; das PDF wird ERST NACH
// Zahlung erzeugt und direkt zum Download bereitgestellt. Ehrliche Abgrenzung zu Tür 2.
export default function EinmalkaufPanel({ content }) {
  const [companyName, setCompanyName] = useState("");
  // V5I-B (V5G §7, Freigabe V5I-A1): Firmenname nur nach ausdrücklicher
  // Zustimmung — null = noch keine Wahl, "ja" | "nein".
  const [companyChoice, setCompanyChoice] = useState(null);
  const [priceCents, setPriceCents] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  // O1C: Produktidentität des geladenen Katalogprodukts — einzige Quelle des Kauf-POSTs.
  const [productId, setProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .getPublicCatalog()
      .then((data) => {
        if (!alive || !data || !Array.isArray(data.products)) return;
        const oneTime = data.products.find(
          (p) => p.pricing && p.pricing.product_type === "one_time",
        );
        if (oneTime) {
          setProductId(oneTime.product_id || null);
          if (oneTime.pricing.one_time_net_cents != null) {
            setPriceCents(oneTime.pricing.one_time_net_cents);
            setCurrency(oneTime.pricing.currency || "EUR");
          }
        }
      })
      .catch(() => {
        /* Preisanzeige optional – Stripe zeigt den verbindlichen Betrag */
      });
    return () => {
      alive = false;
    };
  }, []);

  const buy = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.createOneTimeCheckout({
        origin_url: window.location.origin,
        content,
        company_name: companyName.trim(),
        product_id: productId,
      });
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("no_url");
    } catch (e) {
      setLoading(false);
      const detail = e?.response?.data?.detail;
      if (detail === "checkout_disabled") {
        setError("Der Einmalkauf ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.");
      } else if (detail === "rate_limited") {
        setError("Zu viele Anfragen. Bitte versuchen Sie es in einer Stunde erneut.");
      } else {
        setError("Der Kauf konnte nicht gestartet werden. Bitte versuchen Sie es erneut.");
      }
    }
  };

  return (
    <section className="wrap" data-testid="einmalkauf-panel">
      <div
        style={{
          margin: "18px auto 0",
          maxWidth: 720,
          background: "#0f172a",
          borderRadius: 16,
          padding: "26px 24px",
          color: "#e2e8f0",
          border: "1px solid #1e293b",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#93c5fd",
            fontWeight: 700,
          }}
        >
          <FileDown size={14} /> Ohne Konto
        </span>
        <h3 style={{ margin: "10px 0 6px", fontSize: 22, color: "#f8fafc", lineHeight: 1.2 }}>
          Diese Dokumentation einmalig als PDF kaufen
        </h3>
        <p style={{ margin: "0 0 16px", fontSize: 14.5, color: "#94a3b8", lineHeight: 1.6 }}>
          Sie erhalten genau die gezeigte Dokumentation als PDF. Kein Konto, keine
          Anmeldung, keine wiederkehrenden Kosten.
          <br />
          <span style={{ color: "#cbd5e1" }}>
            Hinweis: Dokumentationsvorschlag ohne revisionssichere Archivierung – kein späterer
            Wiederabruf.
          </span>
        </p>

        {/* V5I-B (V5G §7, Freigabe V5I-A1): vorgeschalteter Ja/Nein-Dialog —
            das Eingabefeld erscheint NUR bei „Ja"; erst danach der Checkout. */}
        <div data-testid="firmenname-dialog" style={{ marginBottom: 16 }}>
          <p style={{ margin: "0 0 8px", fontSize: 14.5, color: "#e2e8f0", fontWeight: 600 }}>
            Möchten Sie Ihren Firmennamen auf der Arbeitsschutzmappe ausgeben?
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              data-testid="firmenname-ja-button"
              onClick={() => setCompanyChoice("ja")}
              style={choiceBtnStyle(companyChoice === "ja")}
            >
              Ja
            </button>
            <button
              type="button"
              data-testid="firmenname-nein-button"
              onClick={() => {
                setCompanyChoice("nein");
                setCompanyName("");
              }}
              style={choiceBtnStyle(companyChoice === "nein")}
            >
              Nein
            </button>
          </div>
        </div>

        {companyChoice === "ja" && (
          <>
            <label
              htmlFor="einmalkauf-company"
              style={{ display: "block", fontSize: 12.5, color: "#cbd5e1", marginBottom: 6 }}
            >
              Firmenname (erscheint auf dem PDF)
            </label>
            <input
              id="einmalkauf-company"
              data-testid="einmalkauf-company-input"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z. B. Mustermann GmbH"
              maxLength={120}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "11px 13px",
                borderRadius: 10,
                border: "1px solid #334155",
                background: "#1e293b",
                color: "#f1f5f9",
                fontSize: 15,
                marginBottom: 16,
              }}
            />
          </>
        )}

        {companyChoice !== null && (
          <>
            {error && (
              <p data-testid="einmalkauf-error" style={{ color: "#fca5a5", fontSize: 14, margin: "0 0 12px" }}>
                {error}
              </p>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
              <button
                type="button"
                data-testid="einmalkauf-buy-btn"
                onClick={buy}
                disabled={loading}
                className="btn btn-light btn-lg"
                style={{ opacity: loading ? 0.7 : 1 }}
              >
                {loading ? <Loader2 size={18} className="spin" /> : <FileDown size={18} />}
                {loading ? "Weiterleitung zu Stripe …" : "Jetzt als PDF kaufen"}
              </button>
              {priceCents != null && (
                <span data-testid="einmalkauf-price" style={{ fontSize: 15, color: "#e2e8f0" }}>
                  <strong style={{ fontSize: 20, color: "#f8fafc" }}>
                    {formatEuro(priceCents)} {currency === "EUR" ? "€" : currency}
                  </strong>{" "}
                  <span style={{ color: "#94a3b8", fontSize: 13 }}>netto, einmalig zzgl. MwSt.</span>
                </span>
              )}
            </div>
          </>
        )}

        <div
          data-testid="einmalkauf-trust"
          style={{ marginTop: 16, fontSize: 12.5, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}
        >
          <ShieldCheck size={14} /> Sichere Zahlung über Stripe · Serverstandort Deutschland
        </div>
      </div>
    </section>
  );
}
